import React, { useState } from 'react';
import styles from './AcademicApprovedTrialBanner.module.css';

interface AcademicApprovedTrialBannerProps {
  topPx: number;
  daysRemaining: number;
  onActivate: () => Promise<void>;
  onContinueTrial: () => void;
}

const AcademicApprovedTrialBanner = ({
  topPx,
  daysRemaining,
  onActivate,
  onContinueTrial,
}: AcademicApprovedTrialBannerProps) => {
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const daysLabel = daysRemaining === 1 ? '1 day' : `${Math.max(daysRemaining, 0)} days`;

  const handleActivate = async () => {
    setActivating(true);
    setError(null);
    try {
      await onActivate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate academic access');
      setActivating(false);
    }
  };

  return (
    <aside
      className={styles.banner}
      style={{ top: topPx }}
      role="region"
      aria-label="Academic application approved"
    >
      <div className={styles.content}>
        <i className={`bi bi-mortarboard ${styles.icon}`} aria-hidden="true" />
        <p className={styles.text}>
          Your academic application was approved. You have <strong>{daysLabel}</strong> left in your trial.
          Activate your academic account now or keep using your trial.
        </p>
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.actions}>
          <button type="button" className={styles.btnPrimary} onClick={handleActivate} disabled={activating}>
            {activating ? 'Activating…' : 'Activate now'}
          </button>
          <button type="button" className={styles.btnSecondary} onClick={onContinueTrial} disabled={activating}>
            Continue with trial
          </button>
        </div>
      </div>
    </aside>
  );
};

export default AcademicApprovedTrialBanner;
